import React, { useContext, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom/cjs/react-router-dom";
import { useDispatch } from "react-redux";
import ProjectHeader from "../../_shared/ProjectHeader/ProjectHeader";
import {
  EXECUTION_BLUEPRINT,
  EXECUTION_BLUEPRINT_REVIEW,
  STRATEGY_CANVAS_VIEW,
} from "../../../../data/RouteUrls";
import {
  ExternalLinkIcon,
  InfoIcon,
} from "../../StrategyCanvas/StrategyCanvasHelpers";
import { useStrategyCanvasStore } from "../../StrategyCanvas/_redux/StrategyCanvasStore";
import { strategyCanvasUrls } from "../../StrategyCanvas/_redux/StrategyCanvasUrls";
import { HeaderContext } from "../../../../HeaderContext";
import { breadCrumbsArray } from "../../../../data/breadCrumbs";
import { setDynamicBreadcrumbs } from "../../_shared/_redux/ProjectActions";
import useExecutionBlueprintStore, {
  useExecutionBlueprintStoreSelector,
} from "../_redux/ExecutionBlueprintStore";
import useExecutionBlueprintReviewStore, {
  useExecutionBlueprintReviewStoreSelector,
} from "./_redux/ExecutionBlueprintReviewStore";
import ExecutionBlueprintReviewMode from "./ExecutionBlueprintReviewMode";
import ExecutionBlueprintBlueprintMode from "./ExecutionBlueprintBlueprintMode";
import ReviewInfoModal from "./ReviewInfoModal";
import "./ExecutionBlueprintReview.scss";

export const MODE_REVIEW = "review";
export const MODE_BLUEPRINT = "blueprint";

// Persisted the same way as Strategy Canvas's own "how review works"
// explainer flag (`scReviewInfoSeen`) - marks that this coach has already
// seen the explainer once, so it only auto-opens the very first time any
// Execution Blueprint review is opened, not on every visit.
const REVIEW_INFO_SEEN_KEY = "ebReviewInfoSeen";

const selectClients = (state) => state.clients;
const selectReviewByUniqueId = (state) => state.reviewByUniqueId;

// ⚠️ "VIEW STRATEGY CANVAS" (UAT, JK #74: "I would have liked a link to the
// Strat Canvas to refer to"). The EXW payloads carry no canvas id — the server
// picks the company's locked canvas itself — so it is looked up by company from
// the Strategy Canvas dashboard, which is keyed the same way (`company_id`).
// Reuses the SC store's cached clients when the coach came from there;
// otherwise one silent GET. Silent on purpose: this is a convenience link, and
// a coach without Strategy Canvas access shouldn't get an error toast for it —
// the link just doesn't appear.
const useStrategyCanvasIdFor = (companyId) => {
  const [canvasId, setCanvasId] = useState(null);
  useEffect(() => {
    if (companyId == null) return undefined;
    let cancelled = false;
    const cached = useStrategyCanvasStore.getState().clients;
    const fromCache = (cached || []).find((c) => c.id === companyId);
    if (fromCache?.briefId) {
      setCanvasId(fromCache.briefId);
      return undefined;
    }
    useStrategyCanvasStore
      .getState()
      ._get(strategyCanvasUrls.GET_DASHBOARD)
      .then((response) => {
        const row = (response.data?.data?.clients || []).find(
          (c) => c.company_id === companyId,
        );
        if (!cancelled) setCanvasId(row?.canvas_unique_id || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [companyId]);
  return canvasId;
};

// Screen wrapper for Execution Blueprint's Review Mode / Blueprint Mode
// toggle - mirrors Strategy Canvas's own StrategyCanvasReview.js. Both modes
// are real: Review Mode is the item-by-item pass, Blueprint Mode is the
// read-only wall-chart grid (ExecutionBlueprintBlueprintMode.js) fed by
// `GET :uniqueId/blueprint-mode`.
//
// This header is deliberately state-independent - identical before and after
// the review is submitted, exactly as Canvas's own review header is. The
// submitted state changes the BODY (read-only items, and a footer CTA that
// swaps to Start Live Review); no live-review button belongs up here. An
// earlier pass did put one in this header, which is what this comment exists
// to stop happening again.
const ExecutionBlueprintReview = () => {
  const history = useHistory();
  const { blueprintId } = useParams();
  const dispatch = useDispatch();
  const [mode, setMode] = useState(MODE_REVIEW);
  const [showInfoModal, setShowInfoModal] = useState(false);

  const clients = useExecutionBlueprintStoreSelector(selectClients);
  const client = (clients || []).find((c) => c.blueprintId === blueprintId);
  const companyName = client?.name || "";
  const strategyCanvasId = useStrategyCanvasIdFor(client?.id);

  const reviewByUniqueId = useExecutionBlueprintReviewStoreSelector(
    selectReviewByUniqueId,
  );
  const review = reviewByUniqueId[blueprintId];
  const reviewedCount = review?.reviewed_count ?? 0;
  const totalItems = review?.total_items ?? null;

  const HeaderMetaData = useContext(HeaderContext);
  useEffect(() => {
    document.title = "Art of Scale | Execution Blueprint Review";
    HeaderMetaData.setHeaderMetadata({ headerTitle: "Execution Blueprint" });
    if (!useExecutionBlueprintStore.getState().clients) {
      useExecutionBlueprintStore.getState().fetchDashboard();
    }
    // Safe/idempotent to call again here too, in case the coach lands on
    // this screen directly (bookmark/back-forward) rather than via the
    // dashboard's "Start Review" button - same reasoning Strategy Canvas's
    // own review screen uses for its own repeat enterReview call.
    useExecutionBlueprintStore.getState().enterReview(blueprintId);
    useExecutionBlueprintReviewStore.getState().fetchReview(blueprintId);
    // eslint-disable-next-line
  }, [blueprintId]);

  useEffect(() => {
    dispatch(
      setDynamicBreadcrumbs(EXECUTION_BLUEPRINT_REVIEW, [
        breadCrumbsArray[EXECUTION_BLUEPRINT][0],
        { title: companyName, url: "" },
      ]),
    );
  }, [companyName]);

  // First-time onboarding - pop the "how review works" explainer open on its
  // own the very first time a coach ever reaches this screen, instead of
  // relying on them noticing the info affordance in the header.
  useEffect(() => {
    if (!localStorage.getItem(REVIEW_INFO_SEEN_KEY)) {
      setShowInfoModal(true);
      localStorage.setItem(REVIEW_INFO_SEEN_KEY, "true");
    }
  }, []);

  return (
    <div>
      <ProjectHeader
        handleNavigation={() => history.push(EXECUTION_BLUEPRINT)}
        showBack
      />
      <div id="execution-blueprint-review">
        <div className="ebr-header">
          <div className="ebr-title">
            Execution Blueprint{companyName ? ` — ${companyName}` : ""}
          </div>
          <div className="ebr-header-right">
            <button
              type="button"
              className="ebr-info-icon"
              onClick={() => setShowInfoModal(true)}
            >
              <InfoIcon size={14} />
              <span>How review works</span>
            </button>

            {strategyCanvasId ? (
              <button
                type="button"
                className="ebr-info-icon"
                onClick={() =>
                  window.open(
                    STRATEGY_CANVAS_VIEW.replace(":briefId", strategyCanvasId),
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
              >
                <span>View Strategy Canvas</span>
                <ExternalLinkIcon size={14} />
              </button>
            ) : null}

            {totalItems !== null ? (
              <div className="ebr-reviewed">
                Reviewed:
                <strong>
                  {" "}
                  {reviewedCount} of {totalItems}
                </strong>
              </div>
            ) : null}

            <div className="ebr-mode-toggle">
              <button
                className={`ebr-mode-btn ${mode === MODE_REVIEW ? "active" : ""}`}
                onClick={() => setMode(MODE_REVIEW)}
              >
                Review Mode
              </button>
              <button
                className={`ebr-mode-btn ${mode === MODE_BLUEPRINT ? "active" : ""}`}
                onClick={() => setMode(MODE_BLUEPRINT)}
              >
                Blueprint Mode
              </button>
            </div>
          </div>
        </div>
        <div className="ebr-divider" />

        {mode === MODE_REVIEW ? (
          <ExecutionBlueprintReviewMode uniqueId={blueprintId} />
        ) : (
          <ExecutionBlueprintBlueprintMode
            uniqueId={blueprintId}
            companyName={companyName}
          />
        )}
      </div>

      {showInfoModal && (
        <ReviewInfoModal onClose={() => setShowInfoModal(false)} />
      )}
    </div>
  );
};

export default ExecutionBlueprintReview;
